import { area } from '@turf/turf';
import { proxy } from 'valtio';
import { isUrbanArea as apiIsUrbanArea } from '~/api/misc2';
import {
  getLayers as apiGetLayers,
  getProject as apiGetProject,
  getProjectFeatures as apiGetProjectFeatures,
} from '~/api/projects2';
import type { Project as BaseProject } from '~/common/types';
import { type LayerDL, layerListFull } from './common';
import { layerMatchesFeature, requestMatchesLayer } from './layerFamily';

export interface Layer {
  id: string;
  /** Legacy `agencyId`s of the geometry owners; a merged record's children carry the ESRI ids and older requests. */
  memberAgencyIds?: string[];
  [key: string]: any;
}

export interface _Layer extends Layer {
  assetsInProjectArea: 'Yes' | 'No';
}

export interface Project extends BaseProject {
  fiberData: {
    geometries: any[];
    updatedAt: Date;
  };
}

export interface AssetLayer {
  id: string;
  name: string;
  type: string;
  sourceTable: string;
  refreshDate: string;
  refreshCadence: string;
  sourceDate: string;
}

export interface RequestableLayer {
  id: string;
  name: string;
  type: string;
  sourceTable: string;
  files: any[];
  status: 'request' | 'requested' | 'pending' | 'complete' | 'cancelled' | 'dismissed' | 'actionRequired';
  notes: string;
  firstEmailed: string | null;
  secondEmailed: string | null;
  thirdEmailed: string | null;
  fourthEmailed: string | null;
  latestEmailed: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  refreshDate: string | null;
  resolutionDate: string | null;
  utilitiesPresent?: string;
}

type TGeom = GeoJSON.Polygon | GeoJSON.MultiPolygon;
const ACRES_CONVERSION = 0.000247105;
const ACREAGE_LIMIT = 40;
const URBAN_ACREAGE_LIMIT = 6;

class Store {
  isLoading: boolean = true;

  project: Project | null = null;
  downloadBoundary: TGeom | undefined;
  exceedsAcreageLimit: boolean = false;
  exceedsUrbanAcreageLimit: boolean = false;

  currentlyAvailable: AssetLayer[] = [];
  noKnownAssets: AssetLayer[] = [];
  dataRequestable: RequestableLayer[] = [];
  asbuiltRequestable: RequestableLayer[] = [];

  downloadLayers: Map<string, LayerDL> = new Map();

  constructor() {
    // NOTE: Parcels are temporarily removed until Braden runs the ESRI 'Repair Geometry'
    // tool on all of our parcel features. Causing issues in the download.
    layerListFull.forEach((layer) => {
      this.downloadLayers.set(layer.title, layer);
    });
  }

  async getUtilityData(projectId: string) {
    const [layers, features, project] = await Promise.all([
      apiGetLayers(projectId),
      apiGetProjectFeatures(projectId),
      apiGetProject(projectId),
    ]);

    if (layers && project) {
      this.isLoading = true;
      const { currentlyAvailable, noKnownAssets, dataRequestable, asbuiltRequestable } = this.formatLayers(
        layers,
        features,
        project,
      );

      this.currentlyAvailable = currentlyAvailable;
      this.noKnownAssets = noKnownAssets;
      this.dataRequestable = dataRequestable;
      this.asbuiltRequestable = asbuiltRequestable;

      this.project = project as unknown as Project;
      this.downloadBoundary = project.geometry;
      this.exceedsAcreageLimit = this.checkAcreage(this.downloadBoundary!);
      this.exceedsUrbanAcreageLimit = await this.checkUrbanAcreage(this.downloadBoundary!);
      this.isLoading = false;
    }
  }

  checkAcreage = (geometry: TGeom) => {
    const areaInMeters = area(geometry);
    const a = areaInMeters * ACRES_CONVERSION;
    if (a > ACREAGE_LIMIT) {
      return true;
    }

    return false;
  };

  checkUrbanAcreage = async (geometry: TGeom) => {
    if (!geometry) return true;
    if (geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon') {
      const isUrban = await apiIsUrbanArea(geometry);

      const areaInMeters = area(geometry);
      const a = areaInMeters * ACRES_CONVERSION;
      if (a > URBAN_ACREAGE_LIMIT && isUrban) {
        return true;
      }
    }

    return false;
  };

  private formatLayers(layers: any[], features: any[], project: { [key: string]: any }) {
    // initial sorting of layers to see if they have assets in the project area.
    // (this is copied from the CG 1.0 Utilities tab, there is probably a better way to do this.)
    const layersResult = layers.map((layer) => {
      const l = { ...layer };
      if (layerMatchesFeature(layer, features)) {
        l['assetsInProjectArea'] = 'Yes';
      }
      return l;
    });

    // filter over layers and sort them into their appropriate category
    // categories are "currentlyAvailble", "noKnownAssets", "dataRequestable", and "asbuiltRequestable"
    const currentlyAvailable = layersResult.filter(
      ({ published, assetsInProjectArea }) => published && assetsInProjectArea === 'Yes',
    );

    const noKnownAssets = layersResult.filter(
      ({ published, assetsInProjectArea }) => published && assetsInProjectArea !== 'Yes',
    );

    let dataRequestable = layersResult.filter(({ published }) => !published);

    // Add Fiber to dataRequestable for detail maps
    const fiber = layersResult.filter((layer) => layer.type === 'Fiber');
    if (fiber) {
      dataRequestable = [...dataRequestable, ...fiber];
    }

    let asbuiltRequestable = layersResult.filter(({ asbuilts_available }) => asbuilts_available);

    const prevAsbuilts = [...asbuiltRequestable];

    // if a layer in the asbuiltsRequestable has no known as assets in the project area, remove it from asbuiltsRequestable.
    asbuiltRequestable = prevAsbuilts.filter((layer) => {
      return !noKnownAssets.includes(layer);
    });

    // if a layer is in dataRequestable and not in asbuiltRequestable, add it to asbuiltRequestable.
    dataRequestable.forEach((requestable) => {
      if (!asbuiltRequestable.includes(requestable) && requestable.asbuilts_available) {
        asbuiltRequestable.push(requestable);
      }
    });

    // Remove duplicates from dataRequestable on id and sourceTable
    dataRequestable = dataRequestable.filter(
      (layer, index, self) => index === self.findIndex((t) => t.id === layer.id && t.sourceTable === layer.sourceTable),
    );

    // Remove fiber/telco from asbuiltRequestable
    asbuiltRequestable = asbuiltRequestable.filter((layer) => !['Telco', 'Fiber', 'Cable'].includes(layer.type));

    // these are the formatted layer arrays that will be returned.
    const ca: AssetLayer[] = [];
    const nka: AssetLayer[] = [];
    const dr: RequestableLayer[] = [];
    const ar: RequestableLayer[] = [];

    // Four forEach loops below to shape the data to how we need it.
    currentlyAvailable.forEach((asset) => {
      const type = this.getAssetType(asset);

      const _ca: AssetLayer = {
        id: asset.id || asset.carrier_id || '',
        name: asset.agencyname || asset.OWNER || '',
        type,
        sourceTable: asset.source_table || '',
        refreshDate: this.formatDate(asset.last_refresh) || '',
        refreshCadence: asset.refresh_cadence || '',
        sourceDate: this.formatDate(asset.source_date) || '',
      };

      ca.push(_ca);
    });

    noKnownAssets.forEach((asset) => {
      const type = this.getAssetType(asset);

      const _nka: AssetLayer = {
        id: asset.id || asset.carrier_id || '',
        name: asset.agencyname || asset.OWNER || '',
        type,
        sourceTable: asset.source_table || '',
        refreshDate: this.formatDate(asset.last_refresh) || '',
        refreshCadence: asset.refresh_cadence || '',
        sourceDate: this.formatDate(asset.source_date) || '',
      };

      nka.push(_nka);
    });

    dataRequestable.forEach((requestable) => {
      const type = this.getAssetType(requestable);

      const _dr: RequestableLayer = {
        id: requestable.id || requestable.carrier_id,
        name: requestable.agencyname || '',
        type,
        sourceTable: requestable.sourceTable || '',
        status: 'request',
        notes: '',
        files: [],
        firstEmailed: null,
        secondEmailed: null,
        thirdEmailed: null,
        fourthEmailed: null,
        latestEmailed: null,
        createdAt: null,
        updatedAt: null,
        refreshDate: null,
        resolutionDate: null,
        utilitiesPresent: requestable.utilitiesPresent,
      };

      const files = this.getFilesForRequest(requestable, project.files || []);

      _dr.files = [...files];

      this.findMatchedRequest(requestable, _dr, 'dataRequested');
      this.findMatchedRequest(requestable, _dr, 'fiberDetailRequested');

      dr.push(_dr);
    });

    asbuiltRequestable.forEach((requestable) => {
      const type = this.getAssetType(requestable);

      const _ar: RequestableLayer = {
        id: requestable.id || requestable.carrier_id,
        name: requestable.agencyname || '',
        type,
        sourceTable: requestable.sourceTable || '',
        status: 'request',
        notes: '',
        files: [],
        firstEmailed: null,
        secondEmailed: null,
        thirdEmailed: null,
        fourthEmailed: null,
        latestEmailed: null,
        createdAt: null,
        updatedAt: null,
        refreshDate: null,
        resolutionDate: null,
        utilitiesPresent: requestable.utilitiesPresent,
      };

      const files = this.getFilesForRequest(requestable, project.files || []);

      _ar.files = [...files];

      this.findMatchedRequest(requestable, _ar, 'asbuiltRequested');

      ar.push(_ar);
    });

    const sortByName = (arr: any[]) => {
      return arr.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
      });
    };

    // return the good stuff
    return {
      currentlyAvailable: sortByName([...ca]),
      noKnownAssets: sortByName([...nka]),
      dataRequestable: sortByName([...dr]),
      asbuiltRequestable: sortByName([...ar]),
    };
  }

  private findMatchedRequest = (
    requestable: any,
    reqLayer: RequestableLayer,
    reqType: 'dataRequested' | 'fiberDetailRequested' | 'asbuiltRequested',
  ) => {
    // NOTE: for matched requests, the "dismissed" status is for internal use.
    // if status is "dismissed", we still allow re-requests. We do not want to match
    // existing "dismissed" requests, as we want it to be a fresh utility request.
    const matchedRequest = requestable.utilityRequests?.find(
      (req: any) => requestMatchesLayer(req, requestable) && req.requestType === reqType && req.status !== 'dismissed',
    );

    // NOTE: the first-fourthEmailed properties are mostly for the .csv export file.
    if (matchedRequest) {
      reqLayer.notes = matchedRequest.publicNotes || '';
      reqLayer.firstEmailed = this.formatDate(matchedRequest.firstEmailed) || null;
      reqLayer.secondEmailed = this.formatDate(matchedRequest.secondEmailed) || null;
      reqLayer.thirdEmailed = this.formatDate(matchedRequest.thirdEmailed) || null;
      reqLayer.fourthEmailed = this.formatDate(matchedRequest.fourthEmailed) || null;
      reqLayer.latestEmailed = this.formatDate(matchedRequest.latestEmailed) || null;
      reqLayer.status = matchedRequest.status || 'request';
      reqLayer.utilitiesPresent = matchedRequest.utilitiesPresent;

      const createdAt = this.formatDate(matchedRequest.createdAt) || null;
      const updatedAt = this.formatDate(matchedRequest.updatedAt) || null;
      const resolutionDate = this.formatDate(matchedRequest.resolutionDate) || null;

      reqLayer.createdAt = createdAt;
      reqLayer.updatedAt = updatedAt;
      reqLayer.resolutionDate = resolutionDate;
    }
  };

  private getFilesForRequest(requestable: { [key: string]: any }, projectFiles: any[]): any[] {
    const files: any[] = [];

    requestable.utilityRequests?.forEach((req: any) => {
      const file = projectFiles.find((f: any) => f.utilityRequest === req.id);

      if (file) {
        files.push(file);
      }
    });

    return files;
  }

  private getAssetType = (asset: { [key: string]: any }) => {
    const type: string = asset.type || 'Unknown';
    return type;
  };

  formatDate(date: any) {
    return Number.isNaN(new Date(date || '').getTime()) ? '' : new Date(date || '').toLocaleDateString();
  }
}

const store = proxy(new Store());
export default store;
